import * as Sentry from '@sentry/react';
import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router';
import { useAuthStore } from '@/stores/authStore';
import { SessionReadyGate } from '@/app/SessionReadyGate';
import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { VerifyEmailPage } from '@/features/auth/VerifyEmailPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { Toaster } from '@/components/ui/sonner';

const DashboardPage = lazy(() =>
  import('@/features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const EditorPage = lazy(() =>
  import('@/features/editor/EditorPage').then((m) => ({ default: m.EditorPage })),
);
const ViewerPage = lazy(() =>
  import('@/features/viewer/ViewerPage').then((m) => ({ default: m.ViewerPage })),
);
const ProjectViewerPage = lazy(() =>
  import('@/features/viewer/ProjectViewerPage').then((m) => ({ default: m.ProjectViewerPage })),
);
const PublicPresentationPage = lazy(() =>
  import('@/features/presentation/player/PublicPresentationPage').then((m) => ({
    default: m.PublicPresentationPage,
  })),
);
const AdminPage = lazy(() =>
  import('@/features/admin/AdminPage').then((m) => ({ default: m.AdminPage })),
);

function PageLoadingFallback() {
  return <div className="h-screen bg-slate-950" />;
}

function PageErrorFallback({ error }: { error: unknown }) {
  return (
    <div className="flex h-screen items-center justify-center bg-slate-950">
      <div className="text-center max-w-md px-6">
        <p className="text-lg font-medium text-slate-200">Что-то пошло не так</p>
        <p className="mt-1 text-sm text-slate-500">
          {error instanceof Error ? error.message : 'Неизвестная ошибка'}
        </p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Гейт админки на клиенте — косметика (прячет UI). Реальная защита — сервер на /api/admin.
// Компонуем поверх ProtectedRoute: auth-гейт и редирект на /login — единый источник там.
function AdminRoute({ children }: { children: React.ReactNode }) {
  const isAdmin = useAuthStore((s) => s.user?.isAdmin);
  return (
    <ProtectedRoute>{isAdmin ? <>{children}</> : <Navigate to="/dashboard" replace />}</ProtectedRoute>
  );
}

// Wrapper ensures EditorPage remounts cleanly when switching between projects.
// React Router reuses the same component instance when only route params change,
// so without key= local state (camera, draw mode, etc.) would leak between projects.
function EditorRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  return (
    <ProtectedRoute>
      <EditorPage key={projectId} />
    </ProtectedRoute>
  );
}

// Wrapper ensures ProjectViewerPage remounts cleanly when switching between projects.
function ProjectViewerRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  return (
    <ProtectedRoute>
      <ProjectViewerPage key={projectId} projectId={projectId!} />
    </ProtectedRoute>
  );
}

export function App() {
  return (
    <>
      <SessionReadyGate>
      <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/register/check-email" element={<Navigate to="/verify-email" replace />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route path="/editor/:projectId" element={
          <Sentry.ErrorBoundary fallback={({ error }) => <PageErrorFallback error={error} />}>
            <EditorRoute />
          </Sentry.ErrorBoundary>
        } />
        <Route path="/project/:projectId/view" element={<ProjectViewerRoute />} />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
        <Route path="/p/:shareToken" element={
          <Sentry.ErrorBoundary fallback={({ error }) => <PageErrorFallback error={error} />}>
            <PublicPresentationPage />
          </Sentry.ErrorBoundary>
        } />
        <Route path="/public/:shareToken" element={
          <Sentry.ErrorBoundary fallback={({ error }) => <PageErrorFallback error={error} />}>
            <ViewerPage />
          </Sentry.ErrorBoundary>
        } />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </Suspense>
      </SessionReadyGate>
      <Toaster />
    </>
  );
}
